import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  approvePartnerBuildings,
  getApprovalStatus,
  getPendingPartnerBuildings,
  rejectPartnerBuildings
} from '~/api/partnerBuildingApproval';
import {
  ApprovalStatusResponse,
  PendingPartnerBuilding
} from '~/types/partnerBuildingApproval';

type LoadingConfig = {
  flag: boolean;
  comment?: string;
};

const defaultLoadingConfig: LoadingConfig = {
  flag: false,
  comment: ''
};

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const DEFAULT_PAGE_SIZE = 50;

const POLL_INTERVAL_MS = 2000;

// How long a freshly queued batch may report inactive before the loop gives up on it. approve
// returns as soon as the jobs are written, so the first poll or two can legitimately come back
// inactive; stopping on that would strand the progress bar at 0%.
const STARTUP_GRACE_MS = 30000;

export default function usePartnerBuildingApproval() {
  const [buildings, setBuildings] = useState<PendingPartnerBuilding[]>([]);
  const [loadingConfig, setLoadingConfig] = useState<LoadingConfig>(defaultLoadingConfig);
  const [showRejected, setShowRejected] = useState(false);
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatusResponse | null>(null);

  // The poll callback is registered once but needs current list args to refetch when a batch
  // finishes. A ref avoids recreating the loop every time the admin changes page.
  const listArgsRef = useRef({ showRejected, page, pageSize });
  listArgsRef.current = { showRejected, page, pageSize };

  // Whether the last poll saw a running batch, so the completion toasts fire on the
  // active -> inactive edge only, and not on every idle poll.
  const wasActiveRef = useRef(false);

  // Set by the polling effect so approveSelected can restart it — the loop stops once a batch
  // drains, and a second approval from the same page would otherwise never be watched.
  const wakePollRef = useRef<() => void>(() => {});

  const fetchBuildings = useCallback(
    async (
      includeRejected: boolean,
      pageToFetch: number,
      sizeToFetch: number,
      refresh: boolean = false
    ) => {
      setLoadingConfig({
        flag: true,
        comment: refresh ? 'Refreshing buildings...' : 'Fetching buildings awaiting approval...'
      });
      try {
        const { data } = await getPendingPartnerBuildings(
          includeRejected,
          pageToFetch,
          sizeToFetch
        );
        setBuildings(data.buildings);
        setTotalPages(data.totalPages);
        setTotalCount(data.totalCount);
        // The last page empties out when its rows are all approved. Step back rather than
        // stranding the admin on a page that no longer exists.
        if (data.totalPages > 0 && pageToFetch > data.totalPages) {
          setPage(data.totalPages);
        }
        // Drop selections for rows that are no longer listed, so a stale selection can't be
        // submitted after a refresh.
        setSelectedUuids((prev) => {
          const stillListed = new Set(data.buildings.map((b) => b.uuid));
          return new Set(Array.from(prev).filter((uuid) => stillListed.has(uuid)));
        });
        setLoadingConfig({ flag: false });
      } catch (error: any) {
        setLoadingConfig({ flag: false });
        toast.error(error?.message || 'Failed to fetch buildings awaiting approval');
      }
    },
    []
  );

  useEffect(() => {
    fetchBuildings(showRejected, page, pageSize);
  }, [showRejected, page, pageSize, fetchBuildings]);

  // Watch an approval batch until it drains, then stop polling entirely. Runs on mount (so a
  // reload mid-batch reconnects) and whenever this page queues a batch.
  //
  // Deliberate consequence: a batch another admin starts after this page goes idle is not seen
  // until the next reload. The alternative is a timer open for the lifetime of the tab.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    // Set when this page queues a batch, so the loop waits out the gap before the first job
    // enters 'processing' rather than stopping early.
    let awaitingStartUntil = 0;

    const poll = async () => {
      let keepPolling = false;

      try {
        const { data } = await getApprovalStatus(null);

        if (cancelled) {
          return;
        }

        // Inside the startup window the seeded bar is left alone, so it does not flash away and
        // back in the moment before the first job starts.
        const stillStarting = !data.active && Date.now() < awaitingStartUntil;
        if (!stillStarting) {
          setApprovalStatus(data.active ? data : null);
        }

        if (data.active) {
          wasActiveRef.current = true;
          keepPolling = true;
        } else if (wasActiveRef.current) {
          // active -> inactive: the batch just finished. Cleared outside setState so a
          // double-invoked updater in StrictMode cannot fire the toasts twice.
          wasActiveRef.current = false;

          if (data.completed > 0) {
            toast.success(
              `${data.completed} building${data.completed === 1 ? '' : 's'} approved`
            );
          }
          data.failures.forEach((f) =>
            toast.error(`Could not approve ${f.buildingName}: ${f.reason}`)
          );

          const { showRejected: r, page: p, pageSize: s } = listArgsRef.current;
          fetchBuildings(r, p, s, true);
        } else {
          // Nothing seen running yet — keep going only while a queued batch still has time to
          // appear. Otherwise this was the mount check finding an idle queue.
          keepPolling = Date.now() < awaitingStartUntil;
        }
      } catch (error) {
        // A failed poll is not worth a toast — the batch is still draining server-side and the
        // next tick picks it back up.
        keepPolling = wasActiveRef.current || Date.now() < awaitingStartUntil;
      }

      // Chained timeout rather than setInterval, so a slow response can't stack requests.
      if (!cancelled && keepPolling) {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    // Restart the loop for a batch this page just queued.
    wakePollRef.current = () => {
      if (cancelled) {
        return;
      }
      awaitingStartUntil = Date.now() + STARTUP_GRACE_MS;
      clearTimeout(timer);
      poll();
    };

    // Mount check: one call to find a batch already in flight.
    poll();

    return () => {
      wakePollRef.current = () => {};
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fetchBuildings]);

  // The current page number carries no meaning under a new scope or page size — start over.
  const changeShowRejected = (next: boolean) => {
    setShowRejected(next);
    setPage(1);
  };

  const changePageSize = (next: number) => {
    setPageSize(next);
    setPage(1);
  };

  // Pending rows, plus claims whose worker died — nothing else releases those, so leaving them
  // unselectable would strand them. A live 'approving' row has a worker on it, so it stays out.
  const actionableBuildings = buildings.filter(
    (b) => b.status === 'pending' || b.isStaleClaim
  );

  const toggleSelection = (uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedUuids((prev) =>
      prev.size === actionableBuildings.length
        ? new Set()
        : new Set(actionableBuildings.map((b) => b.uuid))
    );
  };

  const reportOutcome = (
    succeeded: number,
    failed: { pendingUuid: string; reason: string }[],
    verb: string
  ) => {
    if (succeeded > 0) {
      toast.success(`${succeeded} building${succeeded === 1 ? '' : 's'} ${verb}`);
    }
    // Surface partial failures individually — a bulk action reporting blanket success while
    // some rows silently failed is worse than noisy.
    failed.forEach((f) => toast.error(`Could not ${verb.replace(/ed$/, '')}: ${f.reason}`));
  };

  // Returns as soon as the server accepts the batch — the outcome arrives through polling.
  const approveSelected = async () => {
    if (selectedUuids.size === 0) {
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await approvePartnerBuildings(Array.from(selectedUuids));
      setSelectedUuids(new Set());

      // Seed the progress state so the bar appears before the first poll lands.
      //
      // wasActiveRef is deliberately NOT set here: it gates the completion toasts, so setting it
      // now would make the first inactive poll — normal before the first job starts — read as a
      // finished batch and toast work that has not happened.
      setApprovalStatus({
        queueUuid: data.queueUuid,
        active: true,
        finished: false,
        abandoned: false,
        total: data.jobCount,
        completed: 0,
        failed: 0,
        remaining: data.jobCount,
        failures: []
      });

      wakePollRef.current();

      toast.success(
        `Approving ${data.jobCount} building${data.jobCount === 1 ? '' : 's'} in the background`
      );
    } catch (error: any) {
      toast.error(error?.message || 'Failed to approve buildings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const rejectBuilding = async (uuid: string, rejectionReason?: string) => {
    setIsSubmitting(true);
    try {
      const { data } = await rejectPartnerBuildings([uuid], rejectionReason);
      reportOutcome(data.rejectedCount, data.failed, 'rejected');
      setSelectedUuids((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
      await fetchBuildings(showRejected, page, pageSize, true);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to reject building');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    buildings,
    actionableBuildings,
    loadingConfig,
    showRejected,
    setShowRejected: changeShowRejected,
    selectedUuids,
    toggleSelection,
    toggleSelectAll,
    approveSelected,
    rejectBuilding,
    isSubmitting,
    page,
    setPage,
    pageSize,
    setPageSize: changePageSize,
    totalPages,
    totalCount,
    approvalStatus
  };
}
